import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Target,
  LayoutGrid,
  List,
  Plus,
  Search,
  Filter,
  RefreshCw,
  Settings,
  Download,
  Upload,
  ChevronDown,
  Building2,
  Phone,
  Mail,
  DollarSign,
  Calendar,
  Tag,
  MoreVertical,
  Trash2,
  Edit2,
  Eye,
  TrendingUp,
  Users,
  CheckCircle,
  Clock,
  Loader2,
  X,
  BarChart3,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { KanbanBoard } from './crm/KanbanBoard';
import { StageManager } from './crm/StageManager';
import { DealDetailModal } from './crm/DealDetailModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default Pipeline Stages
const DEFAULT_STAGES = [
  { id: 'new_lead', label: 'New Lead', color: '#64748b' },
  { id: 'contacted', label: 'Contacted', color: '#3b82f6' },
  { id: 'qualified', label: 'Qualified', color: '#8b5cf6' },
  { id: 'proposal', label: 'Proposal', color: '#f59e0b' },
  { id: 'negotiation', label: 'Negotiation', color: '#ef4444' },
  { id: 'closed_won', label: 'Closed Won', color: '#22c55e' },
  { id: 'closed_lost', label: 'Closed Lost', color: '#6b7280' }
];

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color = 'neon-cyan', trend }) => (
  <div className="glass-card p-4">
    <div className="flex items-start justify-between">
      <div className={`w-10 h-10 rounded-lg bg-${color}/20 flex items-center justify-center`}>
        <Icon className={`w-5 h-5 text-${color}`} />
      </div>
      {trend !== undefined && (
        <span className={`text-xs flex items-center ${trend >= 0 ? 'text-neon-green' : 'text-red-500'}`}>
          <TrendingUp className={`w-3 h-3 mr-1 ${trend < 0 ? 'rotate-180' : ''}`} />
          {Math.abs(trend)}%
        </span>
      )}
    </div>
    <div className="mt-3">
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-sm text-slate-400">{label}</div>
      {subValue && <div className="text-xs text-slate-500 mt-1">{subValue}</div>}
    </div>
  </div>
);

// Deal Form Modal
const DealFormModal = ({ isOpen, onClose, deal, stages, onSave }) => {
  const [form, setForm] = useState({
    name: '',
    company: '',
    contact_name: '',
    email: '',
    phone: '',
    value: 0,
    recurring: false,
    priority: 'medium',
    stage: 'new_lead',
    tags: [],
    notes: '',
    expected_close_date: ''
  });
  const [tagInput, setTagInput] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (deal) {
      setForm({
        name: deal.name || '',
        company: deal.company || '',
        contact_name: deal.contact_name || '',
        email: deal.email || '',
        phone: deal.phone || '',
        value: deal.value || 0,
        recurring: deal.recurring || false,
        priority: deal.priority || 'medium',
        stage: deal.stage || 'new_lead',
        tags: deal.tags || [],
        notes: deal.notes || '',
        expected_close_date: deal.expected_close_date?.slice(0, 10) || ''
      });
    } else {
      setForm({
        name: '',
        company: '',
        contact_name: '',
        email: '',
        phone: '',
        value: 0,
        recurring: false,
        priority: 'medium',
        stage: 'new_lead',
        tags: [],
        notes: '',
        expected_close_date: ''
      });
    }
  }, [deal, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Deal name is required');
      return;
    }

    setSaving(true);
    try {
      await onSave(form, deal?.id);
      onClose();
    } catch (error) {
      toast.error('Failed to save deal');
    } finally {
      setSaving(false);
    }
  };

  const handleTagAdd = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h3 className="text-lg font-bold text-white">
            {deal ? 'Edit Deal' : 'Create New Deal'}
          </h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-slate-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-4">
            {/* Deal Name */}
            <div>
              <label className="block text-sm text-slate-300 mb-1">Deal Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                placeholder="e.g., ABC Healthcare Contract"
                required
              />
            </div>

            {/* Company & Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Company</label>
                <input
                  type="text"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  placeholder="Company name"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Contact Name</label>
                <input
                  type="text"
                  value={form.contact_name}
                  onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  placeholder="Contact name"
                />
              </div>
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  placeholder="email@example.com"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Phone</label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            {/* Value & Stage */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm text-slate-300 mb-1">Deal Value ($)</label>
                <div className="flex">
                  <input
                    type="number"
                    value={form.value}
                    onChange={(e) => setForm({ ...form, value: parseFloat(e.target.value) || 0 })}
                    className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-l-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                    placeholder="0"
                  />
                  <label className="flex items-center px-3 bg-white/5 border border-l-0 border-white/10 rounded-r-lg text-slate-400 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.recurring}
                      onChange={(e) => setForm({ ...form, recurring: e.target.checked })}
                      className="mr-2"
                    />
                    /mo
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Stage</label>
                <select
                  value={form.stage}
                  onChange={(e) => setForm({ ...form, stage: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                >
                  {stages.map(s => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-300 mb-1">Priority</label>
                <select
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            {/* Expected Close Date */}
            <div>
              <label className="block text-sm text-slate-300 mb-1">Expected Close Date</label>
              <input
                type="date"
                value={form.expected_close_date}
                onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm text-slate-300 mb-1">Tags</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {form.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    className="px-2 py-1 bg-white/10 rounded-full text-xs text-white flex items-center space-x-1"
                  >
                    <span>{tag}</span>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, tags: form.tags.filter(t => t !== tag) })}
                      className="text-slate-400 hover:text-red-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleTagAdd())}
                  className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none"
                  placeholder="Add tag"
                />
                <button
                  type="button"
                  onClick={handleTagAdd}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"
                >
                  Add
                </button>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm text-slate-300 mb-1">Notes</label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:border-neon-cyan/50 focus:outline-none resize-none"
                placeholder="Additional notes..."
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-neon-cyan text-slate-900 rounded-lg hover:bg-neon-cyan/80 font-medium flex items-center space-x-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  <span>{deal ? 'Update Deal' : 'Create Deal'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, dealName }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-800 rounded-2xl w-full max-w-md p-6 border border-white/10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-400" />
          </div>
          <h3 className="text-lg font-bold text-white">Delete Deal?</h3>
        </div>
        <p className="text-slate-400 mb-6">
          Are you sure you want to delete <strong className="text-white">{dealName}</strong>? 
          This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white flex items-center space-x-2"
          >
            <Trash2 className="w-4 h-4" />
            <span>Delete</span>
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Component
export const MondayCRM = () => {
  // State
  const [view, setView] = useState('board'); // board, list, analytics
  const [deals, setDeals] = useState([]);
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStage, setFilterStage] = useState('');
  const [filterPriority, setFilterPriority] = useState('');

  // Modals
  const [showStageManager, setShowStageManager] = useState(false);
  const [showDealForm, setShowDealForm] = useState(false);
  const [showDealDetail, setShowDealDetail] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [initialStageForNewDeal, setInitialStageForNewDeal] = useState(null);

  // Fetch deals
  const fetchDeals = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStage) params.append('stage', filterStage);
      if (filterPriority) params.append('priority', filterPriority);
      if (searchQuery) params.append('search', searchQuery);

      const res = await fetch(`${API_URL}/api/crm/deals?${params}`);
      if (res.ok) {
        const data = await res.json();
        setDeals(data.deals || []);
      }
    } catch (error) {
      console.error('Failed to fetch deals:', error);
    }
  }, [filterStage, filterPriority, searchQuery]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/stats`);
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  // Fetch pipeline stages
  const fetchStages = async () => {
    try {
      const res = await fetch(`${API_URL}/api/crm/stages`);
      if (res.ok) {
        const data = await res.json();
        if (data.stages && data.stages.length > 0) {
          setStages(data.stages);
        }
      }
    } catch (error) {
      console.error('Failed to fetch stages:', error);
    }
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDeals(), fetchStats(), fetchStages()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDeals]);

  // Refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchDeals(), fetchStats()]);
    setRefreshing(false);
  };

  // Save stages
  const handleSaveStages = async (newStages) => {
    try {
      const res = await fetch(`${API_URL}/api/crm/stages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stages: newStages })
      });
      if (res.ok) {
        setStages(newStages);
      }
    } catch (error) {
      toast.error('Failed to save stages');
    }
  };

  // Save deal
  const handleSaveDeal = async (formData, dealId) => {
    try {
      const url = dealId 
        ? `${API_URL}/api/crm/deals/${dealId}`
        : `${API_URL}/api/crm/deals`;
      
      // Add initial stage if creating from column
      const data = { ...formData };
      if (!dealId && initialStageForNewDeal) {
        data.stage = initialStageForNewDeal;
      }

      const res = await fetch(url, {
        method: dealId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (res.ok) {
        toast.success(dealId ? 'Deal updated' : 'Deal created');
        await fetchDeals();
        await fetchStats();
        setInitialStageForNewDeal(null);
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      throw error;
    }
  };

  // Update deal
  const handleUpdateDeal = async (dealId, updates) => {
    try {
      const res = await fetch(`${API_URL}/api/crm/deals/${dealId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (res.ok) {
        await fetchDeals();
        await fetchStats();
      }
    } catch (error) {
      toast.error('Failed to update deal');
    }
  };

  // Move deal (drag and drop)
  const handleMoveDeal = async (dealId, newStage) => {
    // Optimistic update
    setDeals(deals.map(d => 
      d.id === dealId ? { ...d, stage: newStage } : d
    ));

    try {
      await fetch(`${API_URL}/api/crm/deals/${dealId}/move`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: newStage })
      });
      await fetchStats();
    } catch (error) {
      toast.error('Failed to move deal');
      await fetchDeals(); // Revert on error
    }
  };

  // Delete deal
  const handleDeleteDeal = async () => {
    if (!selectedDeal) return;

    try {
      const res = await fetch(`${API_URL}/api/crm/deals/${selectedDeal.id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        toast.success('Deal deleted');
        setShowDeleteConfirm(false);
        setShowDealDetail(false);
        setSelectedDeal(null);
        await fetchDeals();
        await fetchStats();
      }
    } catch (error) {
      toast.error('Failed to delete deal');
    }
  };

  // Add activity
  const handleAddActivity = async (dealId, activity) => {
    try {
      const res = await fetch(`${API_URL}/api/crm/deals/${dealId}/activities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(activity)
      });

      if (!res.ok) throw new Error('Failed to add activity');
    } catch (error) {
      throw error;
    }
  };

  // Quick actions
  const handleQuickAction = (action, deal) => {
    switch (action) {
      case 'call':
        if (deal.phone) {
          window.open(`tel:${deal.phone}`);
        } else {
          toast.error('No phone number');
        }
        break;
      case 'email':
        if (deal.email) {
          window.open(`mailto:${deal.email}`);
        } else {
          toast.error('No email address');
        }
        break;
      case 'schedule':
        toast.info('Schedule feature coming soon');
        break;
    }
  };

  // Open deal form from specific column
  const handleAddDealToStage = (stageId) => {
    setInitialStageForNewDeal(stageId);
    setSelectedDeal(null);
    setShowDealForm(true);
  };

  // View deal
  const handleViewDeal = (deal) => {
    setSelectedDeal(deal);
    setShowDealDetail(true);
  };

  // Edit deal
  const handleEditDeal = (deal) => {
    setSelectedDeal(deal);
    setShowDealForm(true);
  };

  // Confirm delete
  const handleConfirmDelete = (deal) => {
    setSelectedDeal(deal);
    setShowDeleteConfirm(true);
  };

  // Filter deals for display
  const filteredDeals = deals.filter(deal => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!deal.name?.toLowerCase().includes(q) && 
          !deal.company?.toLowerCase().includes(q) &&
          !deal.contact_name?.toLowerCase().includes(q) &&
          !deal.email?.toLowerCase().includes(q)) {
        return false;
      }
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-neon-cyan animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="monday-crm">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white flex items-center">
            <Target className="w-6 h-6 mr-2 text-neon-cyan" />
            Deals Pipeline
          </h1>
          <p className="text-slate-400 text-sm mt-1">Manage your sales pipeline</p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          <button
            onClick={() => setShowStageManager(true)}
            className="btn-secondary flex items-center space-x-2"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Stages</span>
          </button>
          <button
            onClick={() => {
              setSelectedDeal(null);
              setInitialStageForNewDeal(null);
              setShowDealForm(true);
            }}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Add Deal</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            icon={Target}
            label="Total Deals"
            value={stats.total_deals || 0}
            color="neon-cyan"
          />
          <StatCard
            icon={DollarSign}
            label="Pipeline Value"
            value={`$${((stats.pipeline_value || 0) / 1000).toFixed(0)}K`}
            color="neon-green"
          />
          <StatCard
            icon={CheckCircle}
            label="Won This Month"
            value={stats.won_this_month || 0}
            subValue={stats.won_value ? `$${(stats.won_value / 1000).toFixed(0)}K` : null}
            color="green-400"
          />
          <StatCard
            icon={Clock}
            label="In Progress"
            value={stats.in_progress || 0}
            color="amber-400"
          />
          <StatCard
            icon={TrendingUp}
            label="Win Rate"
            value={`${stats.win_rate || 0}%`}
            trend={stats.win_rate_change}
            color="neon-purple"
          />
        </div>
      )}

      {/* View Toggle & Filters */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        {/* View Toggle */}
        <div className="flex items-center bg-white/5 rounded-lg p-1">
          <button
            onClick={() => setView('board')}
            className={`px-3 py-1.5 rounded-lg text-sm flex items-center space-x-1 transition-colors ${
              view === 'board' ? 'bg-neon-cyan/20 text-neon-cyan' : 'text-slate-400 hover:text-white'
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
            <span>Board</span>
          </button>
          <button
            onClick={() => setView('list')}
            className={`px-3 py-1.5 rounded-lg text-sm flex items-center space-x-1 transition-colors ${
              view === 'list' ? 'bg-neon-cyan/20 text-neon-cyan' : 'text-slate-400 hover:text-white'
            }`}
          >
            <List className="w-4 h-4" />
            <span>List</span>
          </button>
          <button
            onClick={() => setView('analytics')}
            className={`px-3 py-1.5 rounded-lg text-sm flex items-center space-x-1 transition-colors ${
              view === 'analytics' ? 'bg-neon-cyan/20 text-neon-cyan' : 'text-slate-400 hover:text-white'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            <span>Analytics</span>
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search deals..."
              className="pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm w-64 focus:border-neon-cyan/50 focus:outline-none"
            />
          </div>

          <select
            value={filterStage}
            onChange={(e) => setFilterStage(e.target.value)}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-neon-cyan/50 focus:outline-none"
          >
            <option value="">All Stages</option>
            {stages.map(s => (
              <option key={s.id} value={s.id}>{s.label}</option>
            ))}
          </select>

          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:border-neon-cyan/50 focus:outline-none"
          >
            <option value="">All Priorities</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Board View */}
      {view === 'board' && (
        <KanbanBoard
          stages={stages}
          deals={filteredDeals}
          onAddDeal={handleAddDealToStage}
          onViewDeal={handleViewDeal}
          onEditDeal={handleEditDeal}
          onDeleteDeal={handleConfirmDelete}
          onMoveDeal={handleMoveDeal}
          onQuickAction={handleQuickAction}
        />
      )}

      {/* List View */}
      {view === 'list' && (
        <div className="glass-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Deal</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Company</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Value</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Stage</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Priority</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-slate-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDeals.map(deal => {
                const stage = stages.find(s => s.id === deal.stage);
                return (
                  <tr 
                    key={deal.id} 
                    className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                    onClick={() => handleViewDeal(deal)}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{deal.name}</div>
                      {deal.contact_name && (
                        <div className="text-xs text-slate-400">{deal.contact_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-300">{deal.company || '-'}</td>
                    <td className="px-4 py-3">
                      <span className="text-neon-green font-medium">
                        ${deal.value?.toLocaleString() || 0}
                        {deal.recurring && <span className="text-slate-400 text-xs">/mo</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span 
                        className="px-2 py-1 rounded text-xs"
                        style={{ 
                          backgroundColor: stage?.color + '33', 
                          color: stage?.color 
                        }}
                      >
                        {stage?.label || deal.stage}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs capitalize ${
                        deal.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                        deal.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                        deal.priority === 'medium' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {deal.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-1" onClick={e => e.stopPropagation()}>
                        <button
                          onClick={() => handleEditDeal(deal)}
                          className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleConfirmDelete(deal)}
                          className="p-1.5 rounded hover:bg-red-500/20 text-slate-400 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredDeals.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No deals found</p>
                    <button
                      onClick={() => {
                        setSelectedDeal(null);
                        setShowDealForm(true);
                      }}
                      className="mt-2 text-neon-cyan hover:text-neon-cyan/80"
                    >
                      Create your first deal →
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Analytics View */}
      {view === 'analytics' && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Pipeline by Stage */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-bold text-white mb-4">Pipeline by Stage</h3>
            <div className="space-y-3">
              {stages.map(stage => {
                const stageDeals = filteredDeals.filter(d => d.stage === stage.id);
                const stageValue = stageDeals.reduce((sum, d) => sum + (d.value || 0), 0);
                const percentage = filteredDeals.length > 0 
                  ? (stageDeals.length / filteredDeals.length) * 100 
                  : 0;

                return (
                  <div key={stage.id}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-300">{stage.label}</span>
                      <span className="text-slate-400">
                        {stageDeals.length} deals · ${stageValue.toLocaleString()}
                      </span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${percentage}%`, 
                          backgroundColor: stage.color 
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top Deals */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-bold text-white mb-4">Top Deals by Value</h3>
            <div className="space-y-3">
              {filteredDeals
                .filter(d => d.value > 0)
                .sort((a, b) => (b.value || 0) - (a.value || 0))
                .slice(0, 5)
                .map(deal => (
                  <div 
                    key={deal.id}
                    className="flex items-center justify-between p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10"
                    onClick={() => handleViewDeal(deal)}
                  >
                    <div>
                      <div className="font-medium text-white">{deal.name}</div>
                      <div className="text-xs text-slate-400">{deal.company}</div>
                    </div>
                    <span className="text-neon-green font-bold">
                      ${deal.value?.toLocaleString()}
                      {deal.recurring && <span className="text-xs text-slate-400">/mo</span>}
                    </span>
                  </div>
                ))}
              {filteredDeals.filter(d => d.value > 0).length === 0 && (
                <p className="text-center text-slate-500 py-8">No deals with value</p>
              )}
            </div>
          </div>

          {/* Deals by Priority */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-bold text-white mb-4">Deals by Priority</h3>
            <div className="grid grid-cols-2 gap-4">
              {['urgent', 'high', 'medium', 'low'].map(priority => {
                const count = filteredDeals.filter(d => d.priority === priority).length;
                const colors = {
                  urgent: 'bg-red-500/20 text-red-400',
                  high: 'bg-orange-500/20 text-orange-400',
                  medium: 'bg-blue-500/20 text-blue-400',
                  low: 'bg-slate-500/20 text-slate-400'
                };
                return (
                  <div key={priority} className={`p-4 rounded-lg ${colors[priority]}`}>
                    <div className="text-2xl font-bold">{count}</div>
                    <div className="text-sm capitalize">{priority}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="glass-card p-6">
            <h3 className="text-lg font-bold text-white mb-4">Recent Deals</h3>
            <div className="space-y-3">
              {filteredDeals
                .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
                .slice(0, 5)
                .map(deal => (
                  <div 
                    key={deal.id}
                    className="flex items-center justify-between p-3 bg-white/5 rounded-lg cursor-pointer hover:bg-white/10"
                    onClick={() => handleViewDeal(deal)}
                  >
                    <div className="flex items-center space-x-3">
                      <div 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: stages.find(s => s.id === deal.stage)?.color }}
                      />
                      <div>
                        <div className="font-medium text-white">{deal.name}</div>
                        <div className="text-xs text-slate-400">
                          {new Date(deal.updated_at || deal.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                    <span className="text-xs text-slate-400">
                      {stages.find(s => s.id === deal.stage)?.label}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {showStageManager && (
          <StageManager
            isOpen={showStageManager}
            onClose={() => setShowStageManager(false)}
            stages={stages}
            onSave={handleSaveStages}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDealForm && (
          <DealFormModal
            isOpen={showDealForm}
            onClose={() => {
              setShowDealForm(false);
              setSelectedDeal(null);
              setInitialStageForNewDeal(null);
            }}
            deal={selectedDeal}
            stages={stages}
            onSave={handleSaveDeal}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDealDetail && selectedDeal && (
          <DealDetailModal
            isOpen={showDealDetail}
            onClose={() => {
              setShowDealDetail(false);
              setSelectedDeal(null);
            }}
            deal={selectedDeal}
            stages={stages}
            onUpdate={handleUpdateDeal}
            onDelete={handleConfirmDelete}
            onAddActivity={handleAddActivity}
            API_URL={API_URL}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeleteConfirm && selectedDeal && (
          <DeleteConfirmModal
            isOpen={showDeleteConfirm}
            onClose={() => {
              setShowDeleteConfirm(false);
              setSelectedDeal(null);
            }}
            onConfirm={handleDeleteDeal}
            dealName={selectedDeal.name}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default MondayCRM;
